import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

const Lightbox = dynamic(() => import('yet-another-react-lightbox'), { ssr: false });
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/styles.css';
import './gallery.css';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { buildImageUrl } from '../../utils/utils';
import ExitFullscreenIcon from '../Icon/ExitFullscreenIcon';
import FullscreenIcon from '../Icon/FullscreenIcon';
import GridIcon from '../Icon/GridIcon';
import PauseIcon from '../Icon/PauseIcon';
import PlayIcon from '../Icon/PlayIcon';
import { Close, ZoomIn, ZoomOut } from '@mui/icons-material';

export const GalleryView = ({ t, images, projectId, propertyId }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isProperty = !!propertyId;

  const slides = images.map((img) => {
    const isStaging = img.gallery_Images.startsWith('staging/');
    const pathInfo = {
      galleryPath: isProperty ? 'property_gallery' : 'project_gallery',
      pathId: isProperty ? propertyId : projectId,
      fileName: isStaging ? undefined : img.gallery_Images,
      fullPath: isStaging ? img.gallery_Images : undefined,
    };

    return {
      src: buildImageUrl({
        ...pathInfo,
        isDesktop,
        quality: 60,
      }),
      thumbnail: buildImageUrl({
        ...pathInfo,
        isDesktop,
        width: 200,
        height: 150,
        quality: 20,
      }),
      alt: img.alt || '',
    };
  });

  const [index, setIndex] = useState(-1);
  const [maxPreview, setMaxPreview] = useState(5);
  const previewSlides = slides?.slice(0, maxPreview);
  const remaining = slides?.length - maxPreview;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const iconSize = isMobile ? 18 : isTablet ? 24 : 32;
  const iconSizeMui = isMobile ? 29 : isTablet ? 36 : 44;

  const plugins = useMemo(() => {
    if (index < 0) return [];
    return [Counter, Fullscreen, Slideshow, Thumbnails, Zoom];
  }, [index]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setMaxPreview(width < 640 ? 3 : 5);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const gridClass =
    'gallery-grid ' +
    (isMobile
      ? previewSlides.length === 1
        ? 'gallery-grid--mobile-1'
        : previewSlides.length === 2
        ? 'gallery-grid--mobile-2'
        : previewSlides.length === 3
        ? 'gallery-grid--mobile-3'
        : ''
      : previewSlides.length === 1
      ? 'gallery-grid--1'
      : previewSlides.length === 2
      ? 'gallery-grid--2'
      : previewSlides.length === 3
      ? 'gallery-grid--3'
      : previewSlides.length === 4
      ? 'gallery-grid--4'
      : previewSlides.length === 5
      ? 'gallery-grid--5'
      : '');

  return (
    <Box>
      <Box className={gridClass}>
        {previewSlides.map((slide, i) => {
          const isLast = i === previewSlides.length - 1 && remaining > 0;

          return (
            <Box
              key={i}
              className="gallery-item"
              sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }}
              onClick={() => setIndex(i)}
            >
              <Image
                src={slide.src}
                alt={slide.description || `Picture ${i + 1}`}
                width={isMobile ? 360 : 800}
                height={isMobile ? 270 : 600}
                priority={i === 0}
                loading={i === 0 ? 'eager' : 'lazy'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {isLast && (
                <Box className="gallery-overlay">
                  + {remaining} {t('picture')}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Lightbox
        index={index}
        open={index >= 0}
        close={() => setIndex(-1)}
        slides={slides}
        plugins={plugins}
        counter={{
          container: {
            style: {
              top: isMobile ? 4 : 6,
              fontSize: isMobile ? '14px' : isTablet ? '18px' : '24px',
              alignItems: 'center',
            },
          },
        }}
        thumbnails={{
          position: 'bottom',
          showToggle: true,
          gap: isMobile ? 2 : 5,
          imageFit: 'cover',
          padding: 0,
          borderRadius: 8,
          borderColor: 'black',
          hidden: false,
          vignette: false,
        }}
        toolbar={{
          buttons: ['thumbnails', 'zoom', 'slideshow', 'fullscreen', 'close'],
        }}
        carousel={{
          finite: false,
          preload: 2,
        }}
        animation={{
          fade: 300,
          swipe: 500,
        }}
        render={{
          iconThumbnailsVisible: () => <GridIcon width={iconSize} height={iconSize} />,
          iconThumbnailsHidden: () => <GridIcon width={iconSize} height={iconSize} />,
          iconZoomIn: () => <ZoomIn sx={{ width: iconSizeMui, height: iconSizeMui }} />,
          iconZoomOut: () => <ZoomOut sx={{ width: iconSizeMui, height: iconSizeMui }} />,
          iconSlideshowPlay: () => <PlayIcon width={iconSize} height={iconSize} />,
          iconSlideshowPause: () => <PauseIcon width={iconSize} height={iconSize} />,
          iconEnterFullscreen: () => <FullscreenIcon width={iconSize} height={iconSize} />,
          iconExitFullscreen: () => <ExitFullscreenIcon width={iconSize} height={iconSize} />,
          iconClose: () => <Close sx={{ width: iconSizeMui, height: iconSizeMui }} />,
        }}
        styles={{
          toolbar: {
            padding: '16px 24px',
            gap: isMobile ? '4px' : isTablet ? '8px' : '12px',
          },
          icon: {
            width: isTablet ? 40 : 62,
            height: isTablet ? 40 : 62,
          },
        }}
      />
    </Box>
  );
};
